import { zodResolver } from '@hookform/resolvers/zod';
import { AppError, AppErrorCode } from '@repo/error';
import { t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { cn } from '@repo/ui/lib/utils';
import { PasswordInput } from '@repo/ui/password-input';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { authClient } from '@/providers/session';
import { ZPasswordSchema } from '@/types/auth';

export const ZResetPasswordFormSchema = z
  .object({
    password: ZPasswordSchema,
    repeatedPassword: ZPasswordSchema,
  })
  .refine((data) => data.password === data.repeatedPassword, {
    path: ['repeatedPassword'],
    get message() {
      return t('auth.passwordsDonTMatch');
    },
  });

export type TResetPasswordFormSchema = z.infer<typeof ZResetPasswordFormSchema>;

export type ResetPasswordFormProps = {
  className?: string;
  token: string;
};

export const ResetPasswordForm = ({ className, token }: ResetPasswordFormProps) => {
  useLocale();

  const navigate = useNavigate();

  const form = useForm<TResetPasswordFormSchema>({
    values: {
      password: '',
      repeatedPassword: '',
    },
    resolver: zodResolver(ZResetPasswordFormSchema, { error: validationError }),
  });

  const isSubmitting = form.formState.isSubmitting;

  const onFormSubmit = async ({ password }: Omit<TResetPasswordFormSchema, 'repeatedPassword'>) => {
    try {
      await authClient.emailPassword.resetPassword({
        password,
        token,
      });

      await navigate('/auth/signin');

      form.reset();

      toast.success(t('auth.passwordUpdated'), {
        get description() {
          return t('auth.yourPasswordHasBeenUpdatedSuccessfully');
        },
      });
    } catch (err) {
      const error = AppError.parseError(err);

      const errorMessage = match(error.code)
        .with(AppErrorCode.EXPIRED_CODE, () => t('extra.tokenHasExpiredPleaseTryAgain'))
        .with('INVALID_TOKEN', () => t('extra.invalidTokenProvidedPleaseTryAgain'))
        .with('SAME_PASSWORD', () => t('extra.yourNewPasswordCannotBeTheSameAsYourOld'))
        .otherwise(() => t('extra.weEncounteredAnUnknownErrorWhileAttemptingToResetYour'));

      toast.error(t('auth.anErrorOccurred'), {
        description: errorMessage,
      });
    }
  };

  return (
    <Form {...form}>
      <form
        className={cn('flex w-full flex-col gap-y-4', className)}
        onSubmit={form.handleSubmit(onFormSubmit)}
      >
        <fieldset className="flex w-full flex-col gap-y-4" disabled={isSubmitting}>
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.password')}</FormLabel>
                <FormControl>
                  <PasswordInput {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="repeatedPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.repeatPassword')}</FormLabel>
                <FormControl>
                  <PasswordInput {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </fieldset>

        <Button loading={isSubmitting} size="lg" type="submit">
          {isSubmitting ? t('auth.resettingPassword') : t('auth.resetPassword')}
        </Button>
      </form>
    </Form>
  );
};
